import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initSentry } from '@shared/sentry-client';
import { initPlausible } from '@shared/plausible';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './store/ToastContext';
import { App } from './App';
import { ToastContainer } from './components/Toast';
import { setAuthTokenGetter } from './offline/auth-bridge';
import { startAutoFlush } from './offline/queue';
import { supabase } from './auth/supabase';
import './index.css';

// ── Error tracking (Sentry) ────────────────────────────────────
// Must run before React renders so it can capture early errors.
initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  isProd: import.meta.env.PROD,
});

// ── Analytics (Plausible) ────────────────────────────────────────
initPlausible({ domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN });

// ── Offline setup ────────────────────────────────────────────────

// Wire up the auth bridge so the offline queue can obtain fresh tokens
setAuthTokenGetter(async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
});

// Start auto-flushing queued mutations when the browser goes online
startAutoFlush();

// Register service worker in production
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  void navigator.serviceWorker.register('/sw.js');
}

// ── React root ───────────────────────────────────────────────────

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
        <App />
        <ToastContainer />
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
);
